class SouqRushTheme {
  constructor() {
    this.bindMenu();
    this.bindProductForms();
    this.bindVariantSelectors();
    this.bindQuantity();
    this.bindGallery();
    this.bindStickyHeader();
    this.bindCartDrawer();
    this.bindStickyAtc();
  }

  bindMenu() {
    const button = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!button || !menu) return;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      menu.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
    });
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
      button.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
    }));
  }

  bindQuantity() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-change]');
      if (!button) return;
      const input = button.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const step = button.dataset.quantityChange === 'plus' ? 1 : -1;
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindGallery() {
    document.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      const gallery = thumb.closest('[data-gallery]');
      const main = gallery && gallery.querySelector('[data-gallery-main]');
      if (!main) return;
      main.src = thumb.dataset.imageUrl;
      main.srcset = '';
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    });
  }

  bindStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    // Shopify renders each section (including the header) inside its own snug-height
    // wrapper div, which means plain `position:sticky` never actually sticks - there's no
    // extra room in the parent for it to hold within. The header CSS uses position:fixed
    // instead, so this JS reserves the equivalent space by padding the body to match the
    // header's real, current height (which itself changes as the header compresses on scroll).
    const isFixed = header.classList.contains('is-sticky');
    document.body.classList.toggle('has-fixed-header', isFixed);
    if (!isFixed) return;
    const threshold = 40;
    const syncHeaderHeight = () => {
      document.body.style.paddingTop = `${header.offsetHeight}px`;
    };
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
      syncHeaderHeight();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncHeaderHeight);
  }

  bindVariantSelectors() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const selector = root.querySelector('[data-variant-select]');
      const json = root.querySelector('[data-product-json]');
      if (!selector || !json) return;
      const product = JSON.parse(json.textContent);
      selector.addEventListener('change', () => {
        const scope = root.closest('section') || root;
        const variant = product.variants.find((item) => String(item.id) === selector.value);
        if (!variant) return;
        const isSale = variant.compare_at_price > variant.price;
        root.querySelectorAll('[name="id"]').forEach((input) => { input.value = variant.id; });
        scope.querySelectorAll('[data-price]').forEach((el) => { el.textContent = this.formatMoney(variant.price, product.currency_format); });
        scope.querySelectorAll('[data-compare-price]').forEach((el) => {
          el.textContent = isSale ? this.formatMoney(variant.compare_at_price, product.currency_format) : '';
          el.hidden = !isSale;
        });
        scope.querySelectorAll('[data-sale-badge]').forEach((el) => { el.hidden = !isSale; });
        scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
          button.disabled = !variant.available;
          const span = button.querySelector('span');
          if (span) span.textContent = variant.available ? button.dataset.addLabel : button.dataset.soldLabel;
        });
        if (variant.featured_image) {
          const main = scope.querySelector('[data-gallery-main]');
          if (main) { main.src = variant.featured_image.src; main.srcset = ''; }
        }
        const inventory = scope.querySelector('[data-inventory-message]');
        const option = selector.selectedOptions[0];
        if (inventory && option) {
          const quantity = Number(option.dataset.inventory || 0);
          const available = option.dataset.available === 'true';
          inventory.classList.toggle('is-low', available && quantity > 0 && quantity <= 10);
          inventory.classList.toggle('is-out', !available);
          inventory.textContent = !available ? 'Out of stock' : quantity > 10 ? 'In stock' : quantity > 0 ? `Only ${quantity} left` : 'Available to order';
        }
        if (history.replaceState && root.dataset.updateUrl === 'true') {
          const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
        }
      });
    });
  }

  bindProductForms() {
    if (!document.documentElement.dataset) return;
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (form.dataset.ajax !== 'true') return;
        event.preventDefault();
        const button = form.querySelector('[data-add-to-cart]');
        const label = button && button.querySelector('span');
        const old = label && label.textContent;
        if (button) button.disabled = true;
        if (label) label.textContent = 'Adding…';
        try {
          const response = await fetch(`${window.themeRoutes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error((await response.json()).description || 'Unable to add item');
          await this.refreshCartDrawer();
          this.openCartDrawer();
        } catch (error) { this.toast(error.message); }
        finally { if (button) button.disabled = false; if (label) label.textContent = old; }
      });
    });
  }

  bindCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.openCartDrawer();
      });
    });
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-drawer-overlay]')) {
        this.closeCartDrawer();
        return;
      }
      this.handleDrawerClick(event);
    });
    drawer.addEventListener('change', (event) => this.handleDrawerQtyChange(event));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) this.closeCartDrawer();
    });
  }

  openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    this.refreshCartDrawer();
  }

  closeCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
  }

  async refreshCartDrawer() {
    try {
      const cart = await fetch(`${window.themeRoutes.cart}.js`).then((res) => res.json());
      this.renderCartDrawer(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) { /* silent */ }
  }

  renderCartDrawer(cart) {
    const body = document.querySelector('[data-drawer-body]');
    const footer = document.querySelector('[data-drawer-footer]');
    if (!body) return;
    if (!cart.items.length) {
      body.innerHTML = `<div class="cart-drawer__empty"><p>Your cart is empty.</p><a class="sr-button" href="${window.themeRoutes.allProducts || '/collections/all'}">Continue shopping</a></div>`;
      if (footer) footer.hidden = true;
      return;
    }
    if (footer) footer.hidden = false;
    body.innerHTML = cart.items.map((item) => `
      <div class="cart-drawer__item" data-drawer-line="${item.key}">
        <img src="${item.image || ''}" width="64" height="64" alt="${this.escapeHtml(item.product_title)}" loading="lazy">
        <div class="cart-drawer__item-info">
          <p class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</p>
          ${item.variant_title ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
          <div class="cart-drawer__item-row">
            <div class="quantity quantity--sm">
              <button type="button" data-drawer-qty-minus aria-label="Decrease quantity">−</button>
              <input type="number" min="0" value="${item.quantity}" data-drawer-qty-input aria-label="Quantity">
              <button type="button" data-drawer-qty-plus aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="cart-drawer__remove" data-drawer-remove>Remove</button>
          </div>
        </div>
        <div class="cart-drawer__item-price">${this.formatMoney(item.final_line_price, window.themeMoneyFormat)}</div>
      </div>
    `).join('');
    const totalEl = document.querySelector('[data-drawer-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price, window.themeMoneyFormat);
  }

  handleDrawerClick(event) {
    const minus = event.target.closest('[data-drawer-qty-minus]');
    const plus = event.target.closest('[data-drawer-qty-plus]');
    const remove = event.target.closest('[data-drawer-remove]');
    if (!minus && !plus && !remove) return;
    const line = event.target.closest('[data-drawer-line]');
    if (!line) return;
    const key = line.dataset.drawerLine;
    if (remove) {
      this.updateCartLine(key, 0);
      return;
    }
    const input = line.querySelector('[data-drawer-qty-input]');
    if (!input) return;
    let value = Number(input.value || 1);
    value = minus ? Math.max(0, value - 1) : value + 1;
    input.value = value;
    this.updateCartLine(key, value);
  }

  handleDrawerQtyChange(event) {
    const input = event.target.closest('[data-drawer-qty-input]');
    if (!input) return;
    const line = event.target.closest('[data-drawer-line]');
    if (!line) return;
    this.updateCartLine(line.dataset.drawerLine, Math.max(0, Number(input.value || 0)));
  }

  async updateCartLine(line, quantity) {
    try {
      const response = await fetch(`${window.themeRoutes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line, quantity }),
      });
      const cart = await response.json();
      if (!response.ok || !cart.items) {
        this.toast(cart.message || cart.description || 'Could not update cart.');
        await this.refreshCartDrawer();
        return;
      }
      this.renderCartDrawer(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) { this.toast('Could not update cart.'); await this.refreshCartDrawer(); }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
  }

  bindStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;
    const submitBtn = bar.querySelector('[data-sticky-atc-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        const form = document.querySelector('.main-product [data-product-form]');
        if (form) form.requestSubmit();
      });
    }
    // Re-query the trigger button live on every check rather than caching a reference:
    // bundle/upsell apps on the product page can re-render the form and swap in a new
    // button node, which would silently detach a cached IntersectionObserver target.
    const headerOffset = 72;
    let ticking = false;
    const check = () => {
      ticking = false;
      const trigger = document.querySelector('.main-product [data-add-to-cart]:not([data-sticky-atc-submit])');
      if (!trigger) { bar.classList.remove('is-visible'); return; }
      const rect = trigger.getBoundingClientRect();
      bar.classList.toggle('is-visible', rect.bottom < headerOffset);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    return (format || '${{amount}}').replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  toast(message) {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.textContent = message; toast.classList.add('is-visible');
    clearTimeout(this.toastTimer); this.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  }
}
document.addEventListener('DOMContentLoaded', () => new SouqRushTheme());
